import React from "react";
import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Activity,
  Heart,
  Smartphone,
  Watch,
  Upload,
  CheckCircle,
  XCircle,
  RefreshCw,
  X,
  ArrowRight,
  Camera,
} from "lucide-react";
import {
  fetchIntegrations,
  triggerStravaSync,
  disconnectStrava,
} from "../api/integrations";
import type { IntegrationRow } from "../api/integrations";
import { fetchActivity } from "../api/activities";
import { Button } from "../components/Button";
import { Badge } from "../components/Badge";
import { Skel } from "../components/Skeleton";
import {
  ConnectWithStravaButton,
  PoweredByStrava,
  StravaConsentNote,
  STRAVA_MANAGE_APPS_URL,
} from "../components/StravaBrand";
import { StravaCapacityNotice, hasStravaInviteBypass } from "../components/StravaCapacityNotice";
import { useStravaConnectionsFull } from "../hooks/useFeatureFlags";
import { HistoryGateLine } from "../components/HistoryGateNotice";
import { ImportPreferencesPanel } from "../components/ImportPreferencesPanel";
import {
  errorMessage,
  ACTIVITY_KIND_NOUN,
  importSourceLine,
  STRAVA_PUBLIC_ONLY_COPY,
  STRAVA_RECONNECT_COPY,
  STRAVA_SCOPE_ERROR_COPY,
  stravaEmptySyncCopy,
  stravaSyncFailedLine,
} from "@atlas/shared";
import { useImportStatus } from "../hooks/useImportStatus";
import { useHistoryGate } from "../hooks/useHistoryGate";
import {
  forgetStravaPublicOnly,
  hasStravaPublicOnlyHint,
  rememberStravaPublicOnly,
} from "../lib/strava-public-hint";
import { formatClock } from "../components/ImportProgressBanner";
import type { ActivityKind } from "../api/activities";

// ─── Provider metadata ────────────────────────────────────────────────────────

const PROVIDER_META: Record<
  string,
  { name: string; icon: React.ReactNode; caption: string }
> = {
  strava: {
    name: "Strava",
    icon: <Activity size={18} />,
    caption: "Imports your activities, then new ones sync automatically",
  },
  apple_health: {
    name: "Apple Health",
    icon: <Heart size={18} />,
    caption: "Syncs from the Atlas iPhone app",
  },
  health_connect: {
    name: "Health Connect",
    icon: <Smartphone size={18} />,
    caption: "Syncs from the Atlas Android app",
  },
  garmin: {
    name: "Garmin",
    icon: <Watch size={18} />,
    caption: "Garmin syncs to Strava automatically — connect Strava to cover Garmin today",
  },
};

function isRunActive(row: IntegrationRow | undefined): boolean {
  const s = row?.currentRun?.status;
  return s === "queued" || s === "running";
}

// A finished import/sync changes every activity-derived view; without this the
// map serves its cached heatmap (5 min staleTime) with the new track missing.
function invalidateActivityCaches(queryClient: QueryClient) {
  for (const queryKey of [
    ["map/heatmap"],
    ["map/features"],
    ["activities"],
    ["profile/stats"],
    ["profile/activity-heatmap"],
    ["collections"],
    ["achievements"],
  ]) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

// ─── Disconnect confirm ───────────────────────────────────────────────────────

function DisconnectButton({
  providerName,
  onConfirm,
  pending,
  warning,
}: {
  providerName: string;
  onConfirm: () => void;
  pending: boolean;
  /** Extra consequence shown in the confirm state (e.g. data removal). */
  warning?: string;
}) {
  const [confirming, setConfirming] = React.useState(false);

  if (!confirming) {
    return (
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<XCircle size={13} />}
        onClick={() => setConfirming(true)}
      >
        Disconnect
      </Button>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
        Disconnect {providerName}?{warning ? ` ${warning}` : ""}
      </span>
      <Button variant="danger" size="sm" disabled={pending} onClick={onConfirm}>
        {pending ? "…" : "Disconnect"}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
        Cancel
      </Button>
    </span>
  );
}

// ─── Provider card ────────────────────────────────────────────────────────────

function ProviderCard({
  row,
  actions,
  extra,
}: {
  row: IntegrationRow;
  actions?: React.ReactNode;
  extra?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const meta = PROVIDER_META[row.provider];
  const run = row.currentRun;
  const runActive = isRunActive(row);
  const needsReconnect = row.connected && row.needsReconnect === true;
  const needsAttention = row.connected && (needsReconnect || run?.status === "error");
  // Same data as the app-wide import banner: fetching, rate-limit pause, then
  // matching (which carries on long after the sync run itself has finished).
  const importStatus = useImportStatus().data;
  const importLine =
    importStatus?.active && importStatus.provider === row.provider
      ? importSourceLine(importStatus, formatClock)
      : null;
  const importing = runActive || importLine !== null;

  const statusBadge = row.comingSoon ? (
    <Badge variant="neutral">Coming soon</Badge>
  ) : row.manualOnly ? (
    <Badge variant="neutral">Manual — GPX export</Badge>
  ) : needsAttention ? (
    <Badge variant="danger" dot>Needs attention</Badge>
  ) : row.connected ? (
    <Badge variant="success" dot>Connected</Badge>
  ) : (
    <Badge variant="neutral">Not connected</Badge>
  );

  return (
    <div
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--ring-top)",
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "var(--radius-md)",
              background: row.connected ? "var(--accent-soft)" : "var(--surface-sunken)",
              border: `1px solid ${row.connected ? "var(--border-gold)" : "var(--border-subtle)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: row.connected ? "var(--gold-400)" : "var(--text-muted)",
            }}
          >
            {meta?.icon}
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
              {meta?.name ?? row.provider}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, lineHeight: 1.4 }}>
              {meta?.caption}
            </div>
          </div>
        </div>
        {statusBadge}
      </div>

      {/* Sync status / stats line */}
      <div style={{ fontSize: 12, color: "var(--text-muted)", minHeight: 16 }}>
        {importing ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--status-info)" }}>
            <RefreshCw size={12} />
            {importLine ?? `Importing: ${(run?.importedCount ?? 0).toLocaleString("en-GB")} so far`}
          </span>
        ) : needsAttention ? (
          <span style={{ color: "var(--coral-300)" }}>
            {needsReconnect ? STRAVA_RECONNECT_COPY : stravaSyncFailedLine(run?.error)}
          </span>
        ) : (
          <>
            {row.activityCount > 0 && (
              <>
                {row.activityCount} {row.activityCount === 1 ? "activity" : "activities"}
              </>
            )}
            {row.lastSyncAt && (
              <>
                {row.activityCount > 0 && " · "}
                Last sync {new Date(row.lastSyncAt).toLocaleDateString()}
              </>
            )}
            {row.connected && run?.status === "done" && run.importedCount > 0 && (
              <> · {run.importedCount} imported last run</>
            )}
          </>
        )}
      </div>

      {/* Free plan: the moment of truth. Once a source has imported, say
          where the record stops - permanently, not dismissible. */}
      {row.connected && !runActive && !needsAttention && (row.activityCount > 0 || run?.status === "done") && (
        <HistoryGateLine context="source" />
      )}

      {extra}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginTop: "auto",
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {actions}
        </div>
        {row.activityCount > 0 && (
          <button
            onClick={() => {
              void navigate({
                to: "/activities",
                search: { source: row.provider },
              });
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              color: "var(--text-secondary)",
              padding: "4px 6px",
              borderRadius: "var(--radius-sm)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text-primary)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-secondary)"; }}
          >
            View activities <ArrowRight size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── GPX import jobs (kept from the previous page) ───────────────────────────

interface ImportJob {
  id: string;
  filename: string;
  status: "uploading" | "queued" | "processing" | "done" | "duplicate" | "error";
  /** ATL-172: the same file was uploaded before. */
  duplicateOf?: "already_uploaded" | "removed";
  queuedAt?: number;
  errorMsg?: string;
  result?: {
    activityName: string;
    peaks: number;
    sections: number;
    landmarks: number;
  };
  /** Spec 26: the upload's kind is switched off for automatic imports. The
   * manual upload still went through (explicit intent); this is a note. */
  kindNote?: string;
}

function kindExcludedNote(kind: string | undefined): string | undefined {
  if (kind !== "hike" && kind !== "walk" && kind !== "run") return undefined;
  const noun = ACTIVITY_KIND_NOUN[kind as ActivityKind];
  const plural = noun.many.charAt(0).toUpperCase() + noun.many.slice(1);
  return `Imported as a ${noun.one}. ${plural} are off for automatic imports.`;
}

// ATL-172: one honest line once a batch settles, e.g. "2 of 5 files were
// already on your account, so nothing new was added for them."
function duplicateSummary(jobs: ImportJob[]): string {
  const live = jobs.filter((j) => j.status === "duplicate" && j.duplicateOf !== "removed").length;
  const removed = jobs.filter((j) => j.status === "duplicate" && j.duplicateOf === "removed").length;
  const parts: string[] = [];
  if (live > 0) {
    parts.push(`${live} of ${jobs.length} ${jobs.length === 1 ? "file was" : "files were"} already on your account, so nothing new was added for ${live === 1 ? "it" : "them"}.`);
  }
  if (removed > 0) {
    parts.push(`${removed} ${removed === 1 ? "is" : "are"} in Removed activities; restore ${removed === 1 ? "it" : "them"} from Activities.`);
  }
  return parts.join(" ");
}

function JobRow({ job, onDismiss }: { job: ImportJob; onDismiss: () => void }) {
  const subtext = () => {
    if (job.status === "uploading") return "Uploading…";
    if (job.status === "queued") return "Queued for processing…";
    if (job.status === "processing") return "Processing…";
    if (job.status === "error") return job.errorMsg ?? "Failed";
    if (job.status === "duplicate") {
      return job.duplicateOf === "removed"
        ? "Already uploaded and in Removed activities. Restore it from Activities."
        : "Already on your account. Nothing new was added.";
    }
    if (job.result) {
      const parts: string[] = [];
      if (job.result.peaks > 0) parts.push(`${job.result.peaks} peak${job.result.peaks !== 1 ? "s" : ""}`);
      if (job.result.sections > 0) parts.push(`${job.result.sections} trail section${job.result.sections !== 1 ? "s" : ""}`);
      if (job.result.landmarks > 0) parts.push(`${job.result.landmarks} landmark${job.result.landmarks !== 1 ? "s" : ""}`);
      const base = parts.length > 0 ? parts.join(" · ") : "No features matched";
      return job.kindNote ? `${base} · ${job.kindNote}` : base;
    }
    return job.kindNote ? `Complete · ${job.kindNote}` : "Complete";
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "7px 10px",
        background: "var(--surface-sunken)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          color:
            job.status === "done"
              ? "var(--status-success)"
              : job.status === "error"
                ? "var(--status-danger)"
                : "var(--text-muted)",
        }}
      >
        {job.status === "done" ? (
          <CheckCircle size={14} />
        ) : job.status === "error" ? (
          <XCircle size={14} />
        ) : (
          <RefreshCw size={14} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {job.result?.activityName ?? job.filename}
        </div>
        <div
          style={{
            fontSize: 11,
            color: job.status === "error" ? "var(--status-danger)" : "var(--text-muted)",
            marginTop: 1,
          }}
        >
          {subtext()}
        </div>
      </div>
      {job.status === "done" && !job.id.startsWith("tmp_") && (
        <Link
          to="/activities/$id/review"
          params={{ id: job.id }}
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--text-secondary)",
            textDecoration: "none",
            flexShrink: 0,
          }}
        >
          Review matches
        </Link>
      )}
      {(job.status === "done" || job.status === "duplicate" || job.status === "error") && (
        <button
          onClick={onDismiss}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--text-muted)",
            padding: 2,
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}

const POLL_TIMEOUT_MS = 5 * 60 * 1000;

// Also shown by the route analyser drop zone (RouteAnalyserPage).
export const EXPORT_INSTRUCTIONS: Array<{ service: string; steps: string }> = [
  { service: "AllTrails", steps: "Activity → Share → Export GPX (requires AllTrails+)" },
  { service: "OS Maps", steps: "My maps → Route → Export as GPX" },
  { service: "Garmin Connect", steps: "Activity → Export → GPX" },
  { service: "Komoot", steps: "Tour → Download → GPX" },
];

// ─── Add from photos (spec 39 / ATL-166) ──────────────────────────────────────

function PhotoImportCard() {
  const navigate = useNavigate();
  return (
    <div
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--ring-top)",
        padding: 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <span
          style={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: "var(--radius-md)",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-muted)",
          }}
        >
          <Camera size={18} />
        </span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Summit photos</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            For walks with no track: pick photos taken at the top and Atlas finds the hills.
          </div>
        </div>
      </div>
      <Button variant="secondary" size="sm" onClick={() => void navigate({ to: "/add-from-photos" })}>
        Add from photos
      </Button>
    </div>
  );
}

function GpxCard() {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [jobs, setJobs] = React.useState<ImportJob[]>([]);
  const [dragging, setDragging] = React.useState(false);
  const dragDepth = React.useRef(0);
  const jobsRef = React.useRef(jobs);
  jobsRef.current = jobs;

  const hasPending = jobs.some((j) => j.status === "queued" || j.status === "processing");

  React.useEffect(() => {
    if (!hasPending) return;

    const timer = setInterval(async () => {
      const pending = jobsRef.current.filter((j) => j.status === "queued" || j.status === "processing");
      if (pending.length === 0) return;

      const results = await Promise.allSettled(pending.map((j) => fetchActivity(j.id)));

      if (results.some((r) => r.status === "fulfilled" && r.value.activity.processedAt)) {
        invalidateActivityCaches(queryClient);
      }

      setJobs((prev) => {
        let changed = false;
        const next = prev.map((job) => {
          if (job.status !== "queued" && job.status !== "processing") return job;
          if (job.queuedAt && Date.now() - job.queuedAt > POLL_TIMEOUT_MS) {
            changed = true;
            return { ...job, status: "error" as const, errorMsg: "Processing timed out" };
          }
          const ri = pending.findIndex((p) => p.id === job.id);
          if (ri === -1) return job;
          const r = results[ri];
          if (!r || r.status === "rejected") return job;
          const data = r.value;
          if (!data.activity.processedAt) {
            if (job.status === "queued") { changed = true; return { ...job, status: "processing" as const }; }
            return job;
          }
          changed = true;
          return {
            ...job,
            status: "done" as const,
            result: {
              activityName: data.activity.name,
              peaks: data.matchedPeaks.length,
              sections: data.matchedSections.length,
              landmarks: data.matchedLandmarks.length,
            },
          };
        });
        return changed ? next : prev;
      });
    }, 3000);

    return () => clearInterval(timer);
  }, [hasPending, queryClient]);

  async function handleFiles(files: FileList | File[]) {
    for (const file of Array.from(files)) {
      const tempId = `tmp_${Math.random().toString(36).slice(2)}`;
      setJobs((prev) => [...prev, { id: tempId, filename: file.name, status: "uploading" }]);

      try {
        const form = new FormData();
        form.append("gpx", file);
        const res = await fetch(
          `${import.meta.env["VITE_API_URL"] ?? "/api"}/activities/upload`,
          { method: "POST", body: form, credentials: "include" },
        );

        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? "Upload failed");
        }

        const data = (await res.json()) as {
          activityId: string;
          status: string;
          kind?: string;
          kindExcludedByPreference?: boolean;
          duplicate?: boolean;
          reason?: "already_uploaded" | "removed";
        };
        if (data.duplicate) {
          setJobs((prev) =>
            prev.map((j) =>
              j.id === tempId
                ? { ...j, status: "duplicate" as const, duplicateOf: data.reason ?? "already_uploaded" }
                : j,
            ),
          );
          continue;
        }
        const kindNote = data.kindExcludedByPreference ? kindExcludedNote(data.kind) : undefined;
        if (kindNote) {
          setJobs((prev) => prev.map((j) => (j.id === tempId ? { ...j, kindNote } : j)));
        }

        if (data.status === "processed") {
          invalidateActivityCaches(queryClient);
          try {
            const detail = await fetchActivity(data.activityId);
            setJobs((prev) =>
              prev.map((j) =>
                j.id === tempId
                  ? {
                      ...j,
                      id: data.activityId,
                      status: "done" as const,
                      result: {
                        activityName: detail.activity.name,
                        peaks: detail.matchedPeaks.length,
                        sections: detail.matchedSections.length,
                        landmarks: detail.matchedLandmarks.length,
                      },
                    }
                  : j,
              ),
            );
          } catch {
            setJobs((prev) =>
              prev.map((j) => j.id === tempId ? { ...j, id: data.activityId, status: "done" as const } : j),
            );
          }
        } else {
          setJobs((prev) =>
            prev.map((j) =>
              j.id === tempId ? { ...j, id: data.activityId, status: "queued" as const, queuedAt: Date.now() } : j,
            ),
          );
        }
      } catch (err) {
        setJobs((prev) =>
          prev.map((j) =>
            j.id === tempId
              ? { ...j, status: "error" as const, errorMsg: errorMessage(err, "Upload failed") }
              : j,
          ),
        );
      }
    }
  }

  const isUploading = jobs.some((j) => j.status === "uploading");

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault();
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => {
        e.preventDefault();
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) {
          dragDepth.current = 0;
          setDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        const files = Array.from(e.dataTransfer.files).filter((f) =>
          f.name.toLowerCase().endsWith(".gpx"),
        );
        if (files.length > 0) void handleFiles(files);
      }}
      style={{
        background: "var(--surface-card)",
        border: `1px ${dragging ? "dashed" : "solid"} ${dragging ? "var(--border-gold)" : "var(--border-subtle)"}`,
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--ring-top)",
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "var(--radius-md)",
              background: dragging ? "var(--accent-soft)" : "var(--surface-sunken)",
              border: `1px solid ${dragging ? "var(--border-gold)" : "var(--border-subtle)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: dragging ? "var(--gold-400)" : "var(--text-muted)",
            }}
          >
            <Upload size={18} />
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>GPX files</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
              {dragging ? "Drop GPX files to import" : "Upload recorded activities, or drag files onto this card"}
            </div>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".gpx"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files?.length) void handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={isUploading}
          onClick={() => fileRef.current?.click()}
        >
          Upload files
        </Button>
      </div>

      {!isUploading && jobs.some((j) => j.status === "duplicate") && (
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
          {duplicateSummary(jobs)}
        </p>
      )}
      {jobs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {jobs.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              onDismiss={() => setJobs((prev) => prev.filter((j) => j.id !== job.id))}
            />
          ))}
        </div>
      )}

      <div
        style={{
          borderTop: "1px solid var(--border-subtle)",
          paddingTop: 12,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <p className="eyebrow" style={{ fontSize: 10, margin: 0 }}>How to export a GPX</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "4px 20px" }}>
          {EXPORT_INSTRUCTIONS.map(({ service, steps }) => (
            <div key={service} style={{ fontSize: 12, color: "var(--text-muted)" }}>
              <span style={{ fontWeight: 600, color: "var(--text-secondary)" }}>{service}:</span> {steps}
            </div>
          ))}
        </div>
        <Link
          to="/route-analyser"
          style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", display: "inline-flex", alignItems: "center", gap: 4, marginTop: 2 }}
        >
          Planning a walk? Check the route first <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}

// ─── ConnectedSourcesPage ─────────────────────────────────────────────────────

const FORCED_POLL_WINDOW_MS = 60 * 1000;

/** Inline note on the Strava card: a refused grant (danger) or a public-only grant (info). */
function StravaNotice({ tone, children }: { tone: "danger" | "info"; children: React.ReactNode }) {
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      style={{
        fontSize: 12.5,
        lineHeight: 1.5,
        color: tone === "danger" ? "var(--coral-300)" : "var(--text-primary)",
        background: tone === "danger" ? "var(--danger-soft)" : "var(--info-soft)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
        padding: "8px 10px",
      }}
    >
      {children}
    </div>
  );
}

export default function ConnectedSourcesPage() {
  const queryClient = useQueryClient();
  // Providers where a sync was just requested — force polling until the run
  // shows up (the strava run row is created by the worker, not the API).
  const [syncRequestedAt, setSyncRequestedAt] = React.useState<Record<string, number>>({});

  const integrationsQuery = useQuery({
    queryKey: ["integrations"],
    queryFn: fetchIntegrations,
    refetchInterval: (query) => {
      const rows = query.state.data?.integrations ?? [];
      const anyActive = rows.some((r) => isRunActive(r));
      const anyForced = Object.values(syncRequestedAt).some(
        (t) => Date.now() - t < FORCED_POLL_WINDOW_MS,
      );
      return anyActive || anyForced ? 3000 : false;
    },
  });

  const rows = integrationsQuery.data?.integrations ?? [];
  const byProvider = (p: string) => rows.find((r) => r.provider === p);

  // When a sync run finishes (active → idle), the new activities need the same
  // cache bust as a completed GPX import.
  const anyRunActive = rows.some((r) => isRunActive(r));
  // Keep the import status polling through the gap between "Sync now" and the
  // worker creating its run row (the banner's own poll stops while idle).
  const lastSyncRequest = Math.max(0, ...Object.values(syncRequestedAt));
  useImportStatus({
    forcePoll: anyRunActive,
    forcePollUntil: lastSyncRequest ? lastSyncRequest + FORCED_POLL_WINDOW_MS : 0,
  });
  const prevRunActive = React.useRef(false);
  React.useEffect(() => {
    if (prevRunActive.current && !anyRunActive) {
      invalidateActivityCaches(queryClient);
    }
    prevRunActive.current = anyRunActive;
  }, [anyRunActive, queryClient]);

  function markSyncRequested(provider: string) {
    setSyncRequestedAt((prev) => ({ ...prev, [provider]: Date.now() }));
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["integrations"] });
  }

  // ── Strava mutations ──
  const stravaSyncMutation = useMutation({
    mutationFn: triggerStravaSync,
    onSuccess: () => { markSyncRequested("strava"); invalidate(); },
  });
  const stravaDisconnectMutation = useMutation({
    mutationFn: disconnectStrava,
    onSuccess: invalidate,
  });

  const stravaFull = useStravaConnectionsFull();
  const stravaInvited = stravaFull && hasStravaInviteBypass();
  const stravaGated = stravaFull && !stravaInvited;
  // An invited user's connect link carries ?invite=1 so the API-side capacity
  // guard on GET /auth/strava lets them through. force=1 always shows Strava's
  // consent screen: a connect here is deliberate, and a reconnect after a
  // refused grant must offer the activity boxes again.
  const stravaOAuthUrl = `${import.meta.env["VITE_API_URL"] ?? "/api"}/auth/strava?force=1${stravaInvited ? "&invite=1" : ""}`;
  // The OAuth callback lands here with ?strava=connected (Strava API Policy
  // 2.1: confirm to the athlete that the connection succeeded and the import
  // has started). Read once; the URL is tidied so a refresh doesn't repeat it.
  // Same for ?error=strava_scope (the athlete unticked the activity
  // permission on Strava's screen, so nothing was stored) and ?scope=public
  // (connected, but only public activities are visible).
  const [stravaReturn] = React.useState(() => {
    const none = { connected: false, scopeError: false, publicOnly: false };
    try {
      const params = new URLSearchParams(window.location.search);
      const result = {
        connected: params.get("strava") === "connected",
        scopeError: params.get("error") === "strava_scope",
        publicOnly: params.get("scope") === "public",
      };
      if (!result.connected && !result.scopeError && !result.publicOnly) return none;
      params.delete("strava");
      params.delete("scope");
      if (result.scopeError) params.delete("error");
      const qs = params.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
      return result;
    } catch {
      return none;
    }
  });
  const justConnectedStrava = stravaReturn.connected;
  // Public-only grants (ATL-173): this connect's ?scope=public, or one kept
  // from a Strava sign-in. A full-permission connect clears it.
  const [stravaPublicOnly] = React.useState(() => {
    if (stravaReturn.connected && !stravaReturn.publicOnly) {
      forgetStravaPublicOnly();
      return false;
    }
    if (stravaReturn.publicOnly) rememberStravaPublicOnly();
    return stravaReturn.publicOnly || hasStravaPublicOnlyHint();
  });
  const historyTruncated = useHistoryGate().truncated;
  const stravaImportStatus = useImportStatus().data;

  const strava = byProvider("strava");
  // Connected, the last full sync finished and nothing from Strava is on the
  // record: say why rather than leave an empty card (ATL-173).
  const stravaRun = strava?.currentRun;
  const stravaEmpty =
    strava?.connected === true &&
    !strava.needsReconnect &&
    strava.activityCount === 0 &&
    stravaRun?.status === "done" &&
    stravaRun.importedCount === 0 &&
    !(stravaImportStatus?.active && stravaImportStatus.provider === "strava");
  const appleHealth = byProvider("apple_health");
  const healthConnect = byProvider("health_connect");
  const garmin = byProvider("garmin");

  const isLoading = integrationsQuery.isLoading;

  return (
    <div
      style={{
        overflow: "auto",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div
        className="atlas-topo r-page"
        style={{ position: "relative", padding: "28px 28px 24px" }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(0deg, var(--bg-app) 0%, color-mix(in srgb, var(--bg-app) 40%, transparent) 100%)",
          }}
        />
        <div style={{ position: "relative" }}>
          <p className="eyebrow" style={{ fontSize: 11, marginBottom: 4 }}>Data sources</p>
          <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>
            Connected sources
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 6, marginBottom: 0 }}>
            Connect your accounts and import activities to build your exploration record.
          </p>
        </div>
      </div>

      <div
        className="r-page-x"
        style={{
          padding: "0 28px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* Spec 26: one import preference across every automatic source. Sits
            above the cards so the "what would go" labels are read before any
            source-level action. */}
        <ImportPreferencesPanel
          connectedProviders={rows
            .filter((r) => r.connected && !r.comingSoon && !r.manualOnly)
            .map((r) => r.provider as string)}
        />

        {isLoading ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 14 }}>
            {[0, 1, 2, 3, 4].map((i) => <Skel key={i} height="150px" radius="var(--radius-lg)" />)}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 14 }}>
            {/* Strava */}
            {strava && (
              <ProviderCard
                row={strava}
                actions={
                  strava.connected && strava.needsReconnect ? (
                    // Strava refused the connection: Sync now would only fail
                    // again, so the official connect button is the way back.
                    <>
                      {!stravaGated && <ConnectWithStravaButton href={stravaOAuthUrl} height={40} />}
                      <DisconnectButton
                        providerName="Strava"
                        pending={stravaDisconnectMutation.isPending}
                        onConfirm={() => stravaDisconnectMutation.mutate()}
                        warning="This also removes your Strava-imported activities from Atlas."
                      />
                    </>
                  ) : strava.connected ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={stravaSyncMutation.isPending || isRunActive(strava)}
                        leftIcon={<RefreshCw size={13} />}
                        onClick={() => stravaSyncMutation.mutate()}
                      >
                        Sync now
                      </Button>
                      <DisconnectButton
                        providerName="Strava"
                        pending={stravaDisconnectMutation.isPending}
                        onConfirm={() => stravaDisconnectMutation.mutate()}
                        warning="This also removes your Strava-imported activities from Atlas."
                      />
                    </>
                  ) : stravaGated ? undefined : (
                    <ConnectWithStravaButton href={stravaOAuthUrl} height={40} />
                  )
                }
                extra={
                  strava.connected ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {stravaReturn.scopeError && <StravaNotice tone="danger">{STRAVA_SCOPE_ERROR_COPY}</StravaNotice>}
                      {strava.needsReconnect && stravaGated && <StravaCapacityNotice />}
                      {stravaEmpty ? (
                        <StravaNotice tone="info">
                          {stravaEmptySyncCopy({
                            skipped: stravaRun?.skippedCount ?? 0,
                            historyTruncated,
                          })}
                          {!stravaGated && (
                            <>
                              {" "}
                              <a href={stravaOAuthUrl} style={{ color: "var(--text-accent)" }}>
                                Connect again
                              </a>
                            </>
                          )}
                        </StravaNotice>
                      ) : (
                        stravaPublicOnly &&
                        !strava.needsReconnect && <StravaNotice tone="info">{STRAVA_PUBLIC_ONLY_COPY}</StravaNotice>
                      )}
                      {justConnectedStrava && !strava.needsReconnect && (
                        <div
                          role="status"
                          style={{
                            fontSize: 12.5,
                            lineHeight: 1.5,
                            color: "var(--text-primary)",
                            background: "var(--accent-soft)",
                            border: "1px solid var(--border-gold)",
                            borderRadius: "var(--radius-md)",
                            padding: "8px 10px",
                          }}
                        >
                          Strava connected. Your activities are importing now and will be matched
                          as they arrive.
                        </div>
                      )}
                      <PoweredByStrava height={32} style={{ opacity: 0.85 }} />
                      <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text-muted)" }}>
                        Manage or revoke Atlas's access at{" "}
                        <a
                          href={STRAVA_MANAGE_APPS_URL}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "var(--text-accent)", textDecoration: "none" }}
                        >
                          strava.com/settings/apps
                        </a>
                        . Questions about your Strava data:{" "}
                        <a
                          href="mailto:hello@goatlas.co.uk"
                          style={{ color: "var(--text-accent)", textDecoration: "none" }}
                        >
                          hello@goatlas.co.uk
                        </a>
                        .
                      </div>
                    </div>
                  ) : stravaGated ? (
                    <StravaCapacityNotice />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {/* "Try again" is the official Connect with Strava button
                          below (brand rules: the OAuth link is always that). */}
                      {stravaReturn.scopeError && <StravaNotice tone="danger">{STRAVA_SCOPE_ERROR_COPY}</StravaNotice>}
                      <StravaConsentNote align="left" />
                    </div>
                  )
                }
              />
            )}

            {/* Apple Health */}
            {appleHealth && (
              <ProviderCard
                row={appleHealth}
                extra={
                  !appleHealth.connected ? (
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      Install the Atlas iPhone app and connect Apple Health there — walks, runs
                      and hikes import automatically.
                    </div>
                  ) : undefined
                }
              />
            )}

            {/* Health Connect (Android) - on-device importer, spec 25 */}
            {healthConnect && (
              <ProviderCard
                row={healthConnect}
                extra={
                  !healthConnect.connected ? (
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      Install the Atlas Android app and connect Health Connect there — walks,
                      runs and hikes from Garmin Connect, Samsung Health, Fitbit and any other
                      app that writes routes to Health Connect import automatically.
                    </div>
                  ) : undefined
                }
              />
            )}

            {/* Garmin */}
            {garmin && (
              <ProviderCard
                row={garmin}
                extra={
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    Or export a GPX from Garmin Connect (Activity → Export → GPX) and upload it
                    below.
                  </div>
                }
              />
            )}
          </div>
        )}

        <GpxCard />
        <PhotoImportCard />
      </div>
    </div>
  );
}
